/**
 * #1107 — On zone select, connector poll-gate refuse_volume returns Ok(0).
 * Do not trust that read when the fetch was backed off or cached level > 0
 * unless the user explicitly set volume to 0.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  getStartupDisplayVolume,
  normalizeVolumePercent,
  readVolumeCache,
  resolveFetchedVolume,
  resolveInboundDeviceVolume,
} from './volumeHydration';

const userSetZeroRenderers = new Set<string>();
const backedOffSelectVolumeReads = new Set<string>();

export function resetVolumeSelectReadbackForTests(): void {
  userSetZeroRenderers.clear();
  backedOffSelectVolumeReads.clear();
}

/** Record a successful user volume command (slider, keyboard, Prism). */
export function recordUserVolumeLevel(rendererId: string, level: number): void {
  if (!rendererId) return;
  if (normalizeVolumePercent(level) === 0) userSetZeroRenderers.add(rendererId);
  else userSetZeroRenderers.delete(rendererId);
}

export function userExplicitlySetVolumeZero(rendererId: string): boolean {
  return userSetZeroRenderers.has(rendererId);
}

/** Select-path get_volume failed (timeout, unavailable, tiers exhausted). */
export function markSelectVolumeReadBackedOff(rendererId: string): void {
  if (rendererId) backedOffSelectVolumeReads.add(rendererId);
}

function consumeSelectVolumeReadBackedOff(rendererId: string): boolean {
  if (!backedOffSelectVolumeReads.has(rendererId)) return false;
  backedOffSelectVolumeReads.delete(rendererId);
  return true;
}

export type SelectVolumeReadbackResult = {
  level: number;
  skippedSpuriousZero: boolean;
};

export function resolveSelectVolumeReadback(
  rendererId: string,
  fetched: number,
): SelectVolumeReadbackResult {
  const normalized = normalizeVolumePercent(fetched);
  if (normalized !== 0) {
    return { level: resolveFetchedVolume(rendererId, normalized), skippedSpuriousZero: false };
  }

  if (userExplicitlySetVolumeZero(rendererId)) {
    return { level: 0, skippedSpuriousZero: false };
  }

  const readBackedOff = consumeSelectVolumeReadBackedOff(rendererId);
  const cached = readVolumeCache(rendererId);
  const spurious = readBackedOff || (cached != null && cached > 0);

  if (spurious) {
    const keptLevel = cached ?? getStartupDisplayVolume(rendererId);
    diagnosticLog('info', 'volume', 'select volume readback skipped (spurious zero)', {
      rendererId,
      fetched: normalized,
      keptLevel,
      readBackedOff,
      cachedLevel: cached,
    });
    return { level: keptLevel, skippedSpuriousZero: true };
  }

  const fallback = getStartupDisplayVolume(rendererId);
  if (fallback > 0) {
    return { level: fallback, skippedSpuriousZero: false };
  }
  return { level: 0, skippedSpuriousZero: false };
}

/** #1097 post-drag resync — returns null when spurious zero should be ignored. */
export function resolvePostDragVolumeReadback(
  rendererId: string,
  fetched: number,
  lastDragged: number | null,
): number | null {
  const normalized = normalizeVolumePercent(fetched);
  if (normalized === 0 && typeof lastDragged === 'number' && lastDragged > 0) {
    diagnosticLog('info', 'volume', 'Post-drag resync skipped (spurious zero)', {
      rendererId,
      lastDragged,
    });
    return null;
  }
  return resolveInboundDeviceVolume(rendererId, fetched);
}
